import { Input } from '@/components/Input';
import { selectEmployerName, setEmployerName } from '@/context/borrower';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';

export interface IProps {
    baseId: string;
    showErrors: boolean;
}

export const testId = 'EmployerLookup';

/**
 * The employer, as the applicant would say it.
 *
 * This was a business lookup — ABN in Australia, NZBN in New Zealand — that
 * would not let anyone past until they picked a result, and a result is a
 * registered legal name: "WOOLWORTHS GROUP LIMITED", not "Woolworths". Most
 * people do not know the legal name of the company that pays them, so the
 * field was asking for something they could not supply and the journey
 * stopped there.
 *
 * Still required, so the lender always gets a name.
 *
 * What is no longer collected: the ABN, and in NZ the company number and
 * employer address, which came from the same lookup. Manage's `employer` is
 * a free string and `employerABN` is optional, so nothing downstream breaks —
 * and VerifierService already matched on ABN *or* employer name, so it falls
 * back on its own.
 *
 * `employerLookupDetails` is deliberately left in the borrower slice and the
 * Mongo schema: applications saved before this still hold one, and removing
 * the field would orphan that data.
 */
export const EmployerLookup = ({ showErrors, baseId }: IProps): JSX.Element => {
    const employerName = useAppSelector(selectEmployerName);
    const dispatch = useAppDispatch();

    function handleChange(value: string): void {
        dispatch(setEmployerName(value));
    }

    return (
        <div data-testid={testId}>
            <Input
                title='Employer Name'
                placeholder='Woolworths'
                id={`${baseId}-input-employerName`}
                value={employerName ?? ''}
                showErrors={showErrors && !employerName}
                onChange={handleChange}
                tooltipContent='The name of the business you work for, as you would say it.'
            />
        </div>
    );
};
